import React, { useRef, useEffect, useState } from 'react';
import { Award, DollarSign, Calendar, Clock } from 'lucide-react';
import { mockData } from '../data/mock';

const ContractAwardsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="awards" ref={sectionRef} className="py-20 bg-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-100/20 to-indigo-100/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-r from-indigo-100/20 to-blue-100/20 rounded-full blur-3xl"></div>
        
        {/* Floating geometric shapes */}
        <div className="absolute top-20 right-20 w-12 h-12 bg-gradient-to-r from-blue-300/30 to-indigo-300/30 rounded-lg rotate-12 animate-bounce delay-300"></div>
        <div className="absolute bottom-32 left-20 w-8 h-8 bg-gradient-to-r from-indigo-300/30 to-blue-300/30 rounded-full animate-bounce delay-700"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="inline-flex items-center space-x-2 text-sm font-medium text-blue-600 bg-blue-50 px-4 py-2 rounded-full mb-4">
            <Award size={16} />
            <span>Contract Awards</span>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Trusted
            </span>
            <br />
            Government Partner
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our track record of successful government contracts demonstrates our commitment to delivering secure, compliant, and innovative digital solutions.
          </p>
        </div>

        {/* Awards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {mockData.contractAwards.map((award, index) => (
            <div
              key={index}
              className={`group relative transition-all duration-1000 delay-${index * 200} ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
            >
              <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-blue-100/50 relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-indigo-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="relative z-10">
                  {/* Award icon */}
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Award className="h-8 w-8 text-white" />
                  </div>

                  {/* Client */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                    {award.client}
                  </h3>

                  {/* Project */}
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {award.project}
                  </p>

                  {/* Details */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <DollarSign className="h-5 w-5 text-green-500" />
                      <span className="text-lg font-bold text-green-600">{award.value}</span>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-blue-500" />
                      <span className="text-gray-700 font-medium">{award.year}</span>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-indigo-500" />
                      <span className="text-gray-700 font-medium">{award.duration}</span>
                    </div>
                  </div>
                </div>

                {/* Decorative corner */}
                <div className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
              </div>

              {/* Floating accent */}
              <div className="absolute -bottom-4 -left-4 w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full group-hover:animate-bounce"></div>
            </div>
          ))}
        </div>

        {/* Statistics */}
        <div className={`bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl p-8 lg:p-12 relative overflow-hidden transition-all duration-1000 delay-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          {/* Background pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          
          <div className="relative z-10">
            <div className="text-center mb-12">
              <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Government Contract Performance
              </h3>
              <p className="text-blue-100 text-lg max-w-2xl mx-auto">
                Consistently delivering excellence in government and enterprise projects across North America
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">$5.2M+</div>
                <div className="text-blue-100 font-medium">Contract Value</div>
              </div>
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">25+</div>
                <div className="text-blue-100 font-medium">Active Contracts</div>
              </div>
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">100%</div>
                <div className="text-blue-100 font-medium">On-Time Delivery</div>
              </div>
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">15+</div>
                <div className="text-blue-100 font-medium">Years Experience</div>
              </div>
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute -top-4 -left-4 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white/10 rounded-full"></div>
        </div>

        {/* CTA */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <p className="text-lg text-gray-600 mb-6">
            Ready to discuss your next government or enterprise project?
          </p>
          <button 
            onClick={() => window.open(mockData.hero.calendlyLink, '_blank')}
            className="inline-flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-blue-500/25 group"
          >
            Request Consultation
            <Award className="ml-2 h-5 w-5 transition-transform group-hover:rotate-12" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ContractAwardsSection;